import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {MultiSelect} from 'primereact/multiselect'
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react'
import {ReactComponent as FaDoctor} from '../../assets/icons/FaDoctor.svg'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {useListUserWithRoles} from '../healthcare/query-keys/HealthCareQueryManager'
import DialogWrapper from '../new-table/DialogWrapper'
import IconWrapper from '../new-table/IconWrapper'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {
    filterEventTypes,
    filterExplainEvent,
    filterExplainEventTypes,
    getFilterExplainEventKey,
    onFilterChangeListener,
    onFilterExplainChangeListener,
    removeFilterModeChangeListener,
} from './broadcast/FilterEvent'
import {useDatatableContext} from './DataTableContext'

const InitialHCPFilterRequest = {}

const StyledFilterByHealthCarePractitioner = forwardRef(
    ({onFilter = () => {}}, ref) => {
        useSignals()
        useImperativeHandle(ref, () => ({
            showForm: handleShowForm,
            hideForm: handleHideForm,
        }))

        const {translateDatatable, translateCommons, translateOptions} =
            TranslateUtils()

        const {user} = useAuth()
        const {userRole} = useAppConfig()
        const {currentHospital} = useUserHospital()
        const {tableType} = useDatatableContext()

        const panelVisible = useSignal(false)
        const selectedItems = useSignal([])
        const filterByHealthCarePractitionerRef = useRef(null)

        const {data: hcpOptions} = useListUserWithRoles(
            user?.id,
            true,
            null,
            {
                ...InitialHCPFilterRequest,
                hospitalId:
                    userRole === SystemRole.USER_WITH_ROLE
                        ? currentHospital?.id
                        : null,
            },
            !!user?.id,
        )

        const handleShowForm = value => {
            blockOverflownScroll()

            filterByHealthCarePractitionerRef.current.onShow()
        }

        const handleHideForm = () => {
            unblockOverflownScroll()

            filterByHealthCarePractitionerRef.current.onHide()
        }

        const handleChangeSelectedItems = e => {
            selectedItems.value = e.value === null ? [] : e.value
        }

        const handleSubmit = () => {
            onFilterChangeListener(tableType, filterEventTypes.hcp, {
                hcpIds: selectedItems.value,
            })

            let filteredSelectedItems = []

            if (isArrayAndNotEmpty(selectedItems.value)) {
                filteredSelectedItems = hcpOptions?.filter(item =>
                    selectedItems.value.includes(item.value),
                )
            }

            onFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.HCP_FILTER_EXPLAIN_CHANGE,
                filteredSelectedItems,
            )

            handleHideForm()
        }

        const panelVisibilityHandler = () => ({
            optionLabel: 'label',
            optionValue: 'value',
            emptyFilterMessage: translateCommons('No results found'),
            resetFilterOnHide: true,
            filter: true,
            filterBy: 'label',
            filterPlaceholder: translateCommons('Search here'),
            showClear: true,
            onShow: () => (panelVisible.value = true),
            onHide: () => (panelVisible.value = false),
        })

        const panelVisiblilityProps = panelVisibilityHandler()

        const renderPanelFooterTemplate = selectedData => {
            let shouldRenderItemSelected = false
            let itemSelectedLength = 0

            if (!!selectedData && typeof selectedData === 'object') {
                if (isArrayAndNotEmpty(selectedData)) {
                    shouldRenderItemSelected = true

                    itemSelectedLength = selectedData?.length
                }

                if (
                    !shouldRenderItemSelected &&
                    Object.keys(selectedData)?.length > 0
                ) {
                    shouldRenderItemSelected = true

                    itemSelectedLength = Object.keys(selectedData)?.length
                }
            }

            return (
                <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                    <b>
                        {shouldRenderItemSelected && itemSelectedLength > 0
                            ? `${itemSelectedLength} ${
                                  itemSelectedLength > 1 ? 'items' : 'item'
                              } selected`
                            : `No item selected`}
                    </b>
                </div>
            )
        }

        useEffect(() => {
            filterExplainEvent.on(
                `${getFilterExplainEventKey(
                    tableType,
                    filterExplainEventTypes.CLEAR_ALL_FILTER,
                )}`,
                () => {
                    selectedItems.value = []
                },
            )

            filterExplainEvent.on(
                `${getFilterExplainEventKey(
                    tableType,
                    filterExplainEventTypes.CLEAR_HCP_FILTER,
                )}`,
                () => {
                    selectedItems.value = []
                },
            )

            return () => {
                removeFilterModeChangeListener(
                    tableType,
                    filterExplainEventTypes.CLEAR_ALL_FILTER,
                )

                removeFilterModeChangeListener(
                    tableType,
                    filterExplainEventTypes.CLEAR_HCP_FILTER,
                )
            }
        }, [])

        return (
            <DialogWrapper
                header={translateDatatable(
                    'Filter by Health Care Practitioner',
                )}
                ref={filterByHealthCarePractitionerRef}
                onSubmit={handleSubmit}>
                <div className="flex flex-column gap-2">
                    <p className="m-0 p-0 mb-3">
                        {translateDatatable(
                            'Select health care practitioners to display the patient list according to the chosen health care practitioners',
                        )}
                    </p>
                    <MultiSelect
                        className="w-full h-3rem p-inputtext p-0 font-bold"
                        options={
                            isArrayAndNotEmpty(hcpOptions)
                                ? translateOptions(hcpOptions)
                                : []
                        }
                        placeholder={translateCommons('Select an option')}
                        defaultValue={[]}
                        maxSelectedLabels={3}
                        display={
                            selectedItems.value?.length < 4 ? 'chip' : 'comma'
                        }
                        value={selectedItems.value || []}
                        onChange={handleChangeSelectedItems}
                        itemTemplate={item => {
                            return (
                                <div className="flex align-items-center hover:text-700">
                                    <div className="mx-2">
                                        <FaDoctor width={20} height={20} />
                                    </div>
                                    <div className="font-bold">
                                        {item.label}
                                    </div>
                                </div>
                            )
                        }}
                        panelFooterTemplate={() =>
                            renderPanelFooterTemplate(selectedItems.value)
                        }
                        {...panelVisiblilityProps}
                    />
                </div>

                <p className="font-bold">
                    You have selected{' '}
                    <span className="text-darkcyan">
                        {selectedItems.value?.length < 1
                            ? 'no'
                            : selectedItems.value?.length}{' '}
                        health care practitioner(s)
                    </span>{' '}
                    out of{' '}
                    <span className="text-darkcyan">
                        {hcpOptions?.length} available health care
                        practitioner(s)
                    </span>
                </p>

                {selectedItems.value?.length > 0 && (
                    <div>
                        <div className="flex flex-wrap align-items-center gap-1">
                            {selectedItems.value?.map(selectedItemId => {
                                const hcpItem = hcpOptions.find(
                                    item => item.value === selectedItemId,
                                )

                                return (
                                    <div
                                        key={selectedItemId}
                                        className=" m-0 p-0 bg-white p-1 border-round-lg border-lightgreen ">
                                        <IconWrapper
                                            id={selectedItemId + 'filterBy'}
                                            content={hcpItem?.label}
                                            icon={
                                                <FaDoctor
                                                    width={16}
                                                    height={16}
                                                    color="black"
                                                />
                                            }
                                            tooltip={false}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </DialogWrapper>
        )
    },
)

export default StyledFilterByHealthCarePractitioner
